import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContentWide,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { VideoTestData } from './VideoCard';
import { toast } from '@/hooks/use-toast';
import { GripVertical } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface TrafficEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allVideos: VideoTestData[];
  onSave: (distribution: Record<string, number>) => void;
}

export function TrafficEditModal({
  open,
  onOpenChange,
  allVideos,
  onSave
}: TrafficEditModalProps) {
  useLanguage();
  const [distribution, setDistribution] = useState<Record<string, number>>({});
  const [autoAdjust, setAutoAdjust] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ab_test', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast({ title: translateInterfaceText("Sem permissão para editar testes A/B"), variant: 'destructive' });
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);

  useEffect(() => {
    if (open && allVideos.length > 0) {
      const initial = allVideos.reduce((acc, video) => {
        acc[video.id] = video.trafficPercentage;
        return acc;
      }, {} as Record<string, number>);
      setDistribution(initial);
    }
  }, [open, allVideos]);

  const calculateTotalPercentage = () => {
    return Object.values(distribution).reduce((sum, val) => sum + (val || 0), 0);
  };

  const normalizeValue = (value: string): number => {
    // Substitui vírgula por ponto e converte
    const normalized = value.replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? 0 : Math.max(0, Math.min(100, num));
  };

  const formatValue = (value: number): string => {
    return value.toFixed(1).replace('.', ',');
  };

  const handleChange = (videoId: string, value: string, index: number) => {
    const normalizedValue = normalizeValue(value);
    
    setDistribution(prev => {
      const newDist = { ...prev, [videoId]: normalizedValue };
      
      // Se autoajuste estiver ON, ajustar o último valor
      if (autoAdjust && index < allVideos.length - 1) {
        const lastVideoId = allVideos[allVideos.length - 1].id;
        const currentTotal = Object.entries(newDist)
          .filter(([id]) => id !== lastVideoId)
          .reduce((sum, [, val]) => sum + val, 0);
        
        const remaining = Math.max(0, Math.min(100, 100 - currentTotal));
        newDist[lastVideoId] = parseFloat(remaining.toFixed(1));
      }
      
      return newDist;
    });
  };

  const handleSave = () => {
    const total = calculateTotalPercentage();
    
    if (Math.abs(total - 100) > 0.1) { // Tolerância de 0.1%
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: translateInterfaceText("O total deve ser 100%. Atualmente: {value0}%", { value0: (formatValue(total)) }),
        variant: "destructive"
      });
      return;
    }

    onSave(distribution);
    toast({
      title: translateInterfaceText("Distribuição atualizada"),
      description: translateInterfaceText("A distribuição de tráfego foi atualizada com sucesso.")
    });
    onOpenChange(false);
  };

  const totalPercentage = calculateTotalPercentage();
  const isValidTotal = Math.abs(totalPercentage - 100) < 0.1;

  if (allVideos.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentWide>
        <DialogHeader>
          <DialogTitle>{translateInterfaceText("Editar Distribuição de Tráfego")}</DialogTitle>
          <DialogDescription>
            {translateInterfaceText("Ajuste os percentuais de tráfego para cada vídeo")}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Toggle de autoajuste */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex-1">
              <Label htmlFor="auto-adjust" className="text-sm font-medium cursor-pointer">
                {translateInterfaceText("Ajustar automaticamente o último valor para fechar 100%")}</Label>
              <p className="text-xs text-muted-foreground mt-1">
                {translateInterfaceText("Ao editar, o último vídeo será compensado automaticamente")}</p>
            </div>
            <Switch
              id="auto-adjust"
              checked={autoAdjust}
              onCheckedChange={setAutoAdjust}
            />
          </div>

          {/* Tabela de distribuição */}
          <div className="max-h-[56vh] overflow-y-auto">
            <div className="space-y-2">
              {/* Header da tabela */}
              <div className="grid grid-cols-[32px_minmax(280px,1fr)_112px_24px] gap-3 pb-2 border-b border-[#ECEEF2]">
                <div className="text-xs font-semibold text-gray-600">#</div>
                <div className="text-xs font-semibold text-gray-600">{translateInterfaceText("Vídeo")}</div>
                <div className="text-xs font-semibold text-gray-600">{translateInterfaceText("Percentual")}</div>
                <div className="text-xs font-semibold text-gray-600">%</div>
              </div>

              {/* Linhas da tabela */}
              {allVideos.map((video, index) => {
                const isLast = index === allVideos.length - 1;
                const isDisabled = autoAdjust && isLast;
                
                return (
                  <div
                    key={video.id}
                    className="grid grid-cols-[32px_minmax(280px,1fr)_112px_24px] gap-3 items-center p-2.5 bg-[#F9FAFB] rounded-[10px] hover:shadow-[0_1px_0_rgba(0,0,0,0.06)] transition-shadow"
                  >
                    {/* Coluna 1: Drag handle / Índice */}
                    <div className="flex items-center justify-center text-gray-400">
                      <GripVertical className="h-4 w-4" />
                    </div>

                    {/* Coluna 2: Vídeo (thumbnail + nome) */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-14 h-8 overflow-hidden rounded flex-shrink-0">
                        <img 
                          src={video.thumbnail} 
                          alt={video.name} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span 
                        className="text-sm font-medium line-clamp-2"
                        title={video.name}
                      >
                        {video.name}
                      </span>
                    </div>

                    {/* Coluna 3: Input de percentual */}
                    <div>
                      <Input
                        type="text"
                        value={formatValue(distribution[video.id] || 0)}
                        onChange={(e) => handleChange(video.id, e.target.value, index)}
                        disabled={isDisabled}
                        className={`w-[112px] text-right ${isDisabled ? 'bg-gray-100' : ''}`}
                        aria-label={translateInterfaceText("Percentual do vídeo {value0}", { value0: (video.name) })}
                      />
                      {isDisabled && (
                        <p className="text-xs text-gray-500 mt-1">{translateInterfaceText("Auto-ajustado")}</p>
                      )}
                    </div>

                    {/* Coluna 4: Símbolo % */}
                    <div className="text-sm text-gray-600">%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Barra de total */}
          <div className={`p-3 rounded-lg border ${
            isValidTotal 
              ? 'bg-[#E7F8EC] border-[#1F9D55]' 
              : 'bg-[#FDE8E8] border-[#D13636]'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {isValidTotal ? (
                  <Badge className="bg-[#1F9D55] text-white hover:bg-[#1F9D55]">100%</Badge>
                ) : (
                  <span className="text-[#D13636]">
                    {translateInterfaceText("Total atual:")}{" "}{formatValue(totalPercentage)}{translateInterfaceText("% (ajuste para 100%)")}</span>
                )}
              </span>
              <span className={`text-lg font-bold ${
                isValidTotal ? 'text-[#1F9D55]' : 'text-[#D13636]'
              }`}>
                {formatValue(totalPercentage)}%
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translateInterfaceText("Cancelar")}</Button>
          <Button onClick={handleSave} disabled={!isValidTotal && !autoAdjust}>
            {translateInterfaceText("Salvar")}</Button>
        </DialogFooter>
      </DialogContentWide>
    </Dialog>
  );
}
